import {
  buildSelectionFromDefaults,
  buildSelectionFromPartial,
  selectionToFilterParams,
} from '~/shared/utils/cascadingNarrativeFilters';
import type { FilterHierarchyConfig } from '~/shared/utils/filterHierarchy';

/**
 * Temporary FE rule: SARTable ("Performance as of …") is portfolio-level and should not
 * refetch when Company changes. BE still requires company params, so we pin them to stable
 * defaults instead of omitting. Remove once BE declares per-widget filters.
 */
const WIDGETS_PINNING_COMPANY_PARAMS = new Set(['SARTable']);

const COMPANY_FILTER_PARAM_PATTERN = /^(company|company-type|company type)$/i;

export function shouldPinCompanyFilterParams(widgetId?: string | null): boolean {
  return Boolean(widgetId && WIDGETS_PINNING_COMPANY_PARAMS.has(String(widgetId)));
}

/** @deprecated Use shouldPinCompanyFilterParams */
export const shouldOmitCompanyFilterParams = shouldPinCompanyFilterParams;

function pinnedCompanyParams(tree: FilterHierarchyConfig, mode: 'ids' | 'labels'): Record<string, string> {
  // Prefer Public when available (Single Asset Public Mimicking Portfolio).
  const hasPublicRoot = tree.options.some(node => node.id === 'public' || /^public$/i.test(node.name));
  const selection = hasPublicRoot
    ? buildSelectionFromPartial(tree, { 'company-type': 'public' })
    : buildSelectionFromDefaults(tree);
  const params = selectionToFilterParams(tree, selection);
  return mode === 'ids' ? params.ids : params.labels;
}

/**
 * For pinned widgets, replace company hierarchy params with stable defaults so the query
 * key stays constant across Company filter clicks (without dropping required params).
 */
export function pinCompanyFilterParamsForWidget<T extends Record<string, unknown>>(
  filters: T | undefined | null,
  widgetId?: string | null,
  tree?: FilterHierarchyConfig | null,
  mode: 'ids' | 'labels' = 'ids',
): T | undefined | null {
  if (!filters || !shouldPinCompanyFilterParams(widgetId) || !tree?.filterHierarchy?.length) {
    return filters;
  }

  const pinned = pinnedCompanyParams(tree, mode);
  const next: Record<string, unknown> = { ...filters };

  tree.filterHierarchy.forEach(level => {
    delete next[level.id];
    delete next[level.name];
  });
  Object.keys(next).forEach(key => {
    if (COMPANY_FILTER_PARAM_PATTERN.test(key)) delete next[key];
  });

  Object.assign(next, pinned);
  return next as T;
}

/** @deprecated Use pinCompanyFilterParamsForWidget */
export function omitCompanyFilterParams<T extends Record<string, unknown>>(
  filters: T | undefined | null,
  widgetId?: string | null,
  tree?: FilterHierarchyConfig | null,
  mode: 'ids' | 'labels' = 'ids',
): T | undefined | null {
  return pinCompanyFilterParamsForWidget(filters, widgetId, tree, mode);
}
